import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listInvoicesBetween } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `fee-report-section-paid-list` ("Fee Paid
 * Collection wise") — every real invoice paid by a section's students in a
 * date range: student, fee heads/subheads on that invoice, amount paid,
 * date, ledger. Confirmed via `FeeReportService.sectionPaidInvoiceList`
 * directly that this is a flat per-invoice list, not an aggregate — built
 * client-side from the already-exposed `GET /api/fee-invoices/range` — no
 * new backend needed beyond exposing the invoice's own `classConfigurationId`
 * (see MIGRATION.md).
 *
 * Real gap found and fixed while sweeping Fee Management: the real template
 * (`fee-report-section-paid-list.html`) has no academic-year selector at
 * all — the real backend query (`sectionPaidInvoiceList`) filters by
 * `classConfigId` + date range only, nothing else. This page originally
 * added an academic-year filter the real page doesn't have, cross-
 * referencing a year-scoped student roster (`GET /api/students`) instead of
 * the invoice's own class configuration — a student who moved section
 * mid-year, or a `classConfigId` reused loosely, could disagree with the
 * real app's simpler, more direct filter. Now filters directly by the
 * invoice's own `classConfigurationId`, matching the real query exactly.
 */
export default function FeeSectionPaidListPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setRows(null);
    if (!classConfigId) {
      setError("Please select a section.");
      return;
    }
    setLoading(true);
    try {
      const invoices = await listInvoicesBetween(fromDate, toDate);
      const filtered = invoices
        .filter((inv) => String(inv.classConfigurationId) === String(classConfigId))
        .map((inv) => ({
          ...inv,
          feeHeads: Array.from(new Set((inv.lines || []).map((l) => l.feeHeadName))).join(", "),
          feeSubHeads: Array.from(new Set((inv.lines || []).map((l) => l.feeSubHeadName).filter(Boolean))).join(", "),
        }))
        .sort((a, b) => (a.studentRoll || 0) - (b.studentRoll || 0));
      setRows(filtered);
    } catch (err) {
      setError("Failed to load paid list.");
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = (rows || []).reduce((sum, r) => sum + r.paidAmount, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Paid Collection wise</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Select Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                  <th>Fee Heads</th>
                  <th>Fee Sub Heads</th>
                  <th>Paid Amount</th>
                  <th>Payment Date</th>
                  <th>Ledger</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.masterId}>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                    <td>{r.feeHeads}</td>
                    <td>{r.feeSubHeads}</td>
                    <td>{r.paidAmount.toFixed(2)}</td>
                    <td>{r.paymentDate}</td>
                    <td>{r.ledgerName}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8}>No paid invoices found for this selection.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td colSpan={3}>{grandTotal.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
